import { useNode } from "@craftjs/core";
import { FormControlLabel, FormGroup, Switch } from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useThemeStore } from "@state/theme";
import { useEditorStore } from "@state/editor";
import { useConfigStore } from "@state/config";
import { EventFiltersSettings } from "@components/EventFilterSettings";
import { apiClient } from "@api/index";
import { useAuthStore } from "@state/auth";
import { Event } from "@api/routers/events";
import EventStatusChip from "@components/EventStatusChip";

interface EventHeroPreviewComponentProps {
  gradient?: boolean;
  showDots?: boolean;
  connect?: any;
  drag?: any;
  categoryIDs?: string[];
  eventStatus?: string;
  keywords?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  sizing?: {
    height: {
      value: string;
      previewVh: string;
    };
  };
}

interface EventHeroBuilderComponentProps {
  gradient?: boolean;
  showDots?: boolean;
  categoryIDs?: string[];
  eventStatus?: string;
  keywords?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  sizing?: {
    height: {
      value: string;
      previewVh: string;
    };
  };
}

const EventHeroBuilderComponent = (props: EventHeroBuilderComponentProps) => {
  const {
    connectors: { connect, drag },
  } = useNode();

  return (
    <EventHeroPreviewComponent
      {...props}
      connect={connect}
      drag={drag}
    />
  );
};

const formatRuntime = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const mins  = totalMinutes % 60;

  if (hours === 0)           return `${mins}m`;
  if (mins  === 0)           return `${hours}h`;
  return `${hours}h ${String(mins).padStart(2, "0")}m`;
};

export default EventHeroBuilderComponent;

export const EventHeroPreviewComponent = ({ gradient, showDots, connect, drag, categoryIDs, eventStatus, keywords, startDate, endDate, sizing }: EventHeroPreviewComponentProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselItemWidth, setEventItemWidth] = useState(0);
  const accentColor = useThemeStore(({ accentColor }) => accentColor);
  const user = useAuthStore((state) => state.user);
  const isPreview = useEditorStore(({ isPreview }) => isPreview);

  const dummyEvents = Array.from({ length: 10 }, (_, i) => ({
    eventID: `event-${i}`,
    clientID: "1000000000",
    title: "Event Title",
    eventStatus: "new",
    description: "Event Description",
    startDateTime: new Date(),
  }));

  // Fetch events based on filters
  const {
    data: events = dummyEvents,
    isLoading,
    error,
  } = apiClient.event.getFilteredEvents.useQuery(
    {
      clientID: user.clientID as string,
      anyCategories: categoryIDs && categoryIDs.length > 0 ? categoryIDs : undefined,
      eventStatus: eventStatus || undefined,
      keywords: keywords && keywords.length > 0 ? keywords : undefined,
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined,
      includePrivate: true,
    },
    {
      initialData: dummyEvents as any,
    },
  );

  const itemsCount = events.length;

  const handlePrevClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (carouselRef.current) {
      const firstChild = carouselRef.current.firstChild;
      const firstChildFirstChild = firstChild?.firstChild as HTMLElement | null;

      if (firstChildFirstChild) {
        const carouselItemWidth = firstChildFirstChild.getBoundingClientRect().width;
        setEventItemWidth(carouselItemWidth);
      }
    }

    setCurrentIndex((prev) => prev - 1);
  };

  const handleNextClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (carouselRef.current) {
      const firstChild = carouselRef.current.firstChild;
      const firstChildFirstChild = firstChild?.firstChild as HTMLElement | null;

      if (firstChildFirstChild) {
        const carouselItemWidth = firstChildFirstChild.getBoundingClientRect().width;
        setEventItemWidth(carouselItemWidth);
      }
    }
    setCurrentIndex((prev) => prev + 1);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  useEffect(() => {
    if (isHovering) {
      return;
    }

    const timer = setTimeout(() => {
      if (currentIndex < itemsCount - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        setCurrentIndex(0);
      }
    }, 7000);

    return () => clearTimeout(timer);
  }, [currentIndex, isHovering, itemsCount]);

  return (
    <div
      className={`min-h-min w-full h-full`}
      ref={(ref: any) => (connect && drag ? connect(drag(ref)) : undefined)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        height: isPreview ? sizing?.height.previewVh : sizing?.height.value,
      }}
    >
      <div
        ref={carouselRef}
        className={`w-full h-full min-h-min relative overflow-x-hidden`}
      >
        <div
          className={`w-full h-full min-h-min flex hide-scrollbar overflow-x-hidden snap-x snap-mandatory flex-row items-center transition-all duration-700`}
          style={{
            transform: `translateX(-${currentIndex === 0 ? 0 : currentIndex * carouselItemWidth}px)`,
            width: `${itemsCount * 100}%`,
          }}
        >
          {events.length > 0 ? (
            <>
              {events?.map((event, i) => (
                <EventItem
                  item={event as any}
                  key={i}
                  gradient={gradient}
                />
              ))}
            </>
          ) : (
            <>
              {dummyEvents.map((ev) => (
                <div
                  key={ev.eventID}
                  className="flex flex-col w-full h-full mr-4"
                >
                  <div className="w-full aspect-video bg-gray-400 animate-pulse"></div>
                  <div className="w-full h-6 bg-gray-600 animate-pulse"></div>
                </div>
              ))}
            </>
          )}
        </div>
        {isHovering && currentIndex !== 0 && (
          <button
            className="absolute h-full left-0 top-1/2 transform -translate-y-1/2 p-3"
            style={{ color: accentColor }}
            onClick={handlePrevClick}
          >
            <svg
              viewBox="0 0 24 24"
              className="w-8 h-8 fill-current"
            >
              <path d="M15.4 4.6a1 1 0 0 0-1.41 0L6.7 11.3a1 1 0 0 0 0 1.4l7.3 7.3a1 1 0 1 0 1.4-1.4L9.8 12l5.6-5.7a1 1 0 0 0 0-1.4z"></path>
            </svg>
          </button>
        )}
        {isHovering && currentIndex < itemsCount - 1 && (
          <button
            className="absolute h-full right-0 top-1/2 transform -translate-y-1/2 p-3"
            style={{ color: accentColor }}
            onClick={handleNextClick}
          >
            <svg
              viewBox="0 0 24 24"
              className="w-8 h-8 fill-current"
            >
              <path d="M8.6 4.6a1 1 0 0 1 1.4 0l7.3 7.3a1 1 0 0 1 0 1.4l-7.3 7.3a1 1 0 1 1-1.4-1.4L14.2 12 8.6 6.3a1 1 0 0 1 0-1.4z"></path>
            </svg>
          </button>
        )}

        {/* Little horizontal dots that highlight based on the currentIndex  */}
        {showDots && (
          <div className="absolute bottom-4 left-0 w-full flex justify-center">
            <div className="flex items-center space-x-2 rounded-full px-3 py-2 bg-black/70">
              {events.map((item, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${currentIndex === i ? "bg-white w-2.5 h-2.5" : "bg-gray-500"}`}
                ></div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EventHeroSettings = () => {
  const {
    actions: { setProp },
    gradient,
    showDots,
  } = useNode((node) => ({
    gradient: node.data.props.gradient,
    showDots: node.data.props.showDots,
  }));

  return (
    <div className="w-full space-y-4">
      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={showDots}
              onChange={(e) => setProp((prop: any) => (prop.showDots = e.target.checked))}
            />
          }
          label="Show Active Item Indicator"
        />
      </FormGroup>
      {/* <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={gradient}
              onChange={(e) => setProp((prop: any) => (prop.gradient = e.target.checked))}
            />
          }
          label="Apply Gradient Overlay"
        />
      </FormGroup> */}

      <EventFiltersSettings
        includeCategories={true}
        includeEventStatus={true}
        includeKeywords={true}
        includeDateRange={true}
      />
    </div>
  );
};

// default hero image props
EventHeroBuilderComponent.craft = {
  props: {
    showDots: true,
    gradient: true,
    isResizable: true,
    sizing: {
      height: {
        value: "500px",
        previewVh: "50vh",
      },
    },
  },
  related: {
    settings: EventHeroSettings,
  },
  displayName: "Event Hero",
};

interface EventCardProps {
  item: Event;
  gradient?: boolean;
}

const EventItem: React.FC<EventCardProps> = ({ item, gradient }) => {
  const { textColor, primaryColor: primaryButtonColor } = useThemeStore();
  const { thumbMissingUrl } = useConfigStore();

  const description = item.description?.replace(/<\/?[^>]+(>|$)/g, "");

  const isPublished = item.eventStatus === "published";
  const hasRuntime  = !!item.estRuntime;
  const showChip    = !isPublished || (isPublished && hasRuntime);

  return (
    <div className="relative w-full h-full min-h-min flex items-start">
      <img
        className="absolute inset-0 w-full h-full object-cover"
        src={item.videoThumbnail ? item.videoThumbnail : thumbMissingUrl}
        alt="Event thumbnail"
      />
      {gradient && (
        <div className="absolute inset-0 w-full h-full bg-gradient-to-l from-transparent to-black opacity-60"></div>
      )}
      <div className="z-[1] pl-12 py-16 flex flex-col justify-start">
        <div
          className="text-2xl max-w-[26rem] font-semibold tracking-wide leading-tight line-clamp-1 hover:line-clamp-none"
          style={{ color: textColor }}
        >
          {item.title}
        </div>

        <div className="flex flex-row gap-4">
          {item.startDateTime && (
            <div className="text-sm" style={{ color: textColor }}>
              {moment(item.startDateTime).isSameOrAfter(moment(), "day")
                ? <>Upcoming {moment(item.startDateTime).format("dddd Do")}</>
                : <>{moment(item.startDateTime).format("dddd, MMM Do YYYY [at] h:mm a")}</>}
            </div>
          )}

          {showChip && (
            isPublished && hasRuntime
              ? (
                  <div className="px-1 py-0.5 rounded-md text-[10px] font-semibold uppercase bg-black/70 text-white">
                    {formatRuntime(item.estRuntime!)}
                  </div>
                )
              : (
                  <EventStatusChip status={item.eventStatus} />
                )
          )}
        </div>

        <div
          className="text-base font-semibold max-w-md line-clamp-3"
          style={{ color: textColor }}
        >
          {description}
        </div>

        <div
          rel="noopener noreferrer"
          className="px-14 w-min mt-2 whitespace-nowrap text-center py-2 block font-bold rounded-md shadow-lg hover:shadow"
          style={{ color: textColor, backgroundColor: primaryButtonColor }}
        >
          Watch Now
        </div>
      </div>
    </div>
  );
};
